import React from "react";
import { Bookmark, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { College } from "@/hooks/useCollegeSearch";
import { Skeleton } from "@/components/ui/skeleton";
import { CollegeLogo } from "./CollegeLogo";

interface CollegeListProps {
  colleges: (College & { isSaved?: boolean; savedStatus?: string; savedNotes?: string })[];
  loading: boolean;
  onToggleSave: (collegeId: string) => void;
  showSavedDetails?: boolean;
  onCollegeClick?: (college: College) => void;
  onSuggestCorrection?: (collegeId: string) => void;
}

const CollegeList: React.FC<CollegeListProps> = ({ 
  colleges, 
  loading, 
  onToggleSave,
  showSavedDetails = false,
  onCollegeClick,
  onSuggestCorrection
}) => {
  console.log("🎯 CollegeList - colleges being rendered:", colleges);
  
  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Skeleton className="h-7 w-1/3" />
                  <Skeleton className="h-10 w-10 rounded-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <div className="flex gap-2 mt-4">
                  <Skeleton className="h-8 w-24" />
                  <Skeleton className="h-8 w-24" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (colleges.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>No colleges found matching your criteria.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {colleges.map((college) => (
        <Card 
          key={college.id} 
          className="overflow-hidden cursor-pointer"
          onClick={() => onCollegeClick && onCollegeClick(college)}
        >
          <CardContent className="p-0">
            <div className="p-6">
              {/* College header with name and logo */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">{college.name}</h3>
                <CollegeLogo logoUrl={college.school_logo} collegeName={college.name} size="md" />
              </div>
              
              {/* College details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-gray-600">
                    {college.city}, {college.state}
                  </p>
                  <p className="text-gray-600">
                    {college.public_private} • {college.religious_affiliation && `${college.religious_affiliation} • `}
                    {college.undergraduate_enrollment ? `${college.undergraduate_enrollment.toLocaleString()} students` : 'Enrollment N/A'}
                  </p>
                </div>
                
                <div>
                  <p className="text-gray-600">
                    <span className="font-medium">Division:</span> {college.division || 'N/A'}
                  </p>
                  {college.conference && (
                    <p className="text-gray-600">
                      <span className="font-medium">Conference:</span> {college.conference}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Financial information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">In-State Tuition</p>
                  <p className="font-medium">{college.in_state_tuition ? `$${college.in_state_tuition.toLocaleString()}` : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Out-of-State Tuition</p>
                  <p className="font-medium">{college.out_state_tuition ? `$${college.out_state_tuition.toLocaleString()}` : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Avg. Test Scores</p>
                  <p className="font-medium">
                    {college.average_sat_score ? `SAT: ${college.average_sat_score}` : ''}
                    {college.average_sat_score && college.average_act_score ? ' • ' : ''}
                    {college.average_act_score ? `ACT: ${college.average_act_score}` : ''}
                    {!college.average_sat_score && !college.average_act_score ? 'N/A' : ''}
                  </p>
                </div>
              </div>
              
              {/* Show saved status and notes if available and requested */}
              {showSavedDetails && college.savedStatus && (
                <div className="mb-4 p-3 bg-blue-50 rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge>{college.savedStatus}</Badge>
                  </div>
                  {college.savedNotes && (
                    <p className="text-sm text-gray-700">{college.savedNotes}</p>
                  )}
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  variant={college.isSaved ? "secondary" : "outline"}
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSave(college.id);
                  }}
                >
                  <Bookmark className="h-4 w-4" fill={college.isSaved ? "currentColor" : "none"} />
                  {college.isSaved ? "Saved" : "Save College"}
                </Button>
                
                {college.main_website && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    asChild
                    onClick={(e) => e.stopPropagation()}
                  >
                    <a href={college.main_website} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Visit Website
                    </a>
                  </Button>
                )}
                
                {college.application_website && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    asChild
                    onClick={(e) => e.stopPropagation()}
                  >
                    <a href={college.application_website} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Apply
                    </a>
                  </Button>
                )}
              </div>
              
              {/* Suggest correction link */}
              {onSuggestCorrection && (
                <div className="mt-2 text-right">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onSuggestCorrection(college.id);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                  >
                    <span>See something off? Suggest a fix</span>
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default CollegeList;
